import throttle from "lodash/throttle";
import "core-js/modules/es.array.includes";
import "whatwg-fetch";

// Main modules
import Header from "@/Header";
import ReadingHeader from "@/ReadingHeader";
import FiltersHeader from "@/FiltersHeader";
import SpecialismsHeader from "@/SpecialismsHeader";

// Other modules
import Actionable from "@/Actionable";
import Hero from "@/Hero";
import "@/Container";
import TextSocial from "@/Text";
import "@/404";
import "@/Footer";
import "@/AdvertisingBanner";
import Select from "@/Select";
import TagsList from "@/TagsList";
import LatestArticles from "@/LatestArticles";
import LatestPublications from "@/LatestPublications";
import OurSpecialisms from "@/OurSpecialisms";
// import ContactForm from "@/ContactForm";
import Article from "@/Article";
import Publication from "@/Publication";
import SearchResults from "@/Search";
import Breadcrumb from "@/Breadcrumb";
import Team from "@/Team";
import EventsOverview from "@/EventsOverview";
import ArticleShare from "@/ArticleShare";
import "@/ArticleIcons";
import ArticleMember from "@/ArticleMember";
import ArticleMembersSlider from "@/ArticleMembersSlider";
import ArticlesAndPublicationsGrid from "@/ArticlesAndPublicationsGrid";
import "@/Specialism";
// import SearchForm from "@/SearchForm";
import CookieBanner from "@/CookieBanner";
// import Form from "@/Form";
import GatedFile from "@/GatedFile";
import ImportantMessageBanner from "@/ImportantMessageBanner";
import SelectBox from "@/SelectBox";
import SpecialismsList from "@/SpecialismsList";
import SearchBox from "@/SearchBox";

import "@/Grid";
import "@/Block-TitleTextCta";
import "@/TalkUsBanner";
import "@/NewsletterCta";
import "@/BannerWebsites";
import "@/LabelCount";
import "@/ArticleCard";
import "@/ArticlesArchive";
import "@/PublicationArchive";
import SpecialismsCarousel from "@/SpecialismsCarousel";
import "@/RelatedPublications";
import About from "@/About";
import "@/Page";
import "@/SearchCard";
import "@/ThankYou";
import "@/Checkboxes";
import TalkToUsFlybtn from "@/TalkToUsFlybtn";

// modals
import GatedFileModal from "@/modals/GatedFileModal";
import SignupModal from "@/modals/SignupModal";
import TalkToUsModal from "@/modals/TalkToUsModal";
import RequestCopyModal from "@/modals/RequestCopyModal";

import { tablet, laptop } from "./breakpoints";

// normalize css
import "./normalize.css";

// load global styles
import "./base.css";
import "./typography.css";
import "./common.css";
import "./buttons.css";
import "./wp.css";

const MODULES = [
  Actionable,
  Hero,
  Select,
  TagsList,
  LatestArticles,
  LatestPublications,
  Publication,
  Breadcrumb,
  OurSpecialisms,
  TextSocial,
  ArticlesAndPublicationsGrid,
  ArticleMember,
  ArticleMembersSlider,
  EventsOverview,
  // ContactForm,
  SearchResults,
  // SearchForm,
  Team,
  About,
  ArticleShare,
  Article,
  CookieBanner,
  // Form,
  GatedFile,
  ImportantMessageBanner,
  SelectBox,
  SpecialismsList,
  SpecialismsCarousel,
  TalkToUsFlybtn
];

// modals
const MODALS = {
  GatedFileModal,
  SignupModal,
  TalkToUsModal,
  RequestCopyModal
};

// main modules
const MAIN_MODULES = {
  theHeader: Header,
  theReadingHeader: ReadingHeader,
  theFiltersHeader: FiltersHeader,
  theSpecialismsHeader: SpecialismsHeader,
  theSearchBox: SearchBox
};

(function() {
  if (typeof window.CustomEvent === "function") return false;

  function CustomEvent(event, params) {
    params = params || { bubbles: false, cancelable: false, detail: undefined };
    var evt = document.createEvent("CustomEvent");
    evt.initCustomEvent(
      event,
      params.bubbles,
      params.cancelable,
      params.detail
    );
    return evt;
  }

  CustomEvent.prototype = window.Event.prototype;

  window.CustomEvent = CustomEvent;
})();

class App {
  constructor() {
    window.addEventListener("DOMContentLoaded", this.initApp);
    window.addEventListener("resize", throttle(this.onResize, 150));
    window.addEventListener("scroll", throttle(this.onScroll, 150));
  }

  getCurrentView() {
    const isTablet = window.innerWidth >= tablet;
    const isLaptop = window.innerWidth >= laptop;

    if (isLaptop) {
      return "laptop";
    }

    if (isTablet) {
      return "tablet";
    }

    return "mobile";
  }

  initApp = () => {
    this.lastIndex = 0;
    this.currentView = this.getCurrentView();

    this.instances = [];
    this.modals = {};

    const main = document.querySelector("#main");
    if (main) {
      history.replaceState(
        {
          title: document.title,
          main: main.innerHTML,
          bodyClassAttribute: document.body.getAttribute("class")
        },
        document.title,
        window.location.href
      );
    }

    this.initArea(document);
  };

  initArea = parentElement => {
    if (!parentElement) {
      return [];
    }

    // Create module instances
    const newInstances = MODULES.filter(module =>
      module.hasOwnProperty("selector")
    ).flatMap(Module => {
      const blocks = [...parentElement.querySelectorAll(Module.selector)];
      return blocks
        .filter(block => !block.dataset["instanceIndex" + Module.name])
        .map(block => {
          const previousIndexes = block.dataset.instanceIndex;
          block.dataset.instanceIndex = previousIndexes
            ? [previousIndexes, this.lastIndex].join(",")
            : this.lastIndex;
          block.dataset["instanceIndex" + Module.name] = this.lastIndex;

          this.lastIndex++;
          return new Module(block);
        });
    });

    // Create modals instances
    Object.keys(MODALS).forEach(k => {
      const Modal = MODALS[k];

      if (!Modal.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Modal.selector);
      if (!block) {
        return;
      }

      // TODO: consider multiple instance per element, if multiple modules share
      //       same selector
      if (block.dataset["instanceIndex" + Modal.name]) {
        return;
      }

      const previousIndexes = block.dataset.instanceIndex;
      block.dataset.instanceIndex = previousIndexes
        ? [previousIndexes, this.lastIndex].join(",")
        : this.lastIndex;
      block.dataset["instanceIndex" + Modal.name] = this.lastIndex;
      this.lastIndex++;

      this.modals[k] = new Modal(block);

      newInstances.push(this.modals[k]);
    });

    // Create main module instances
    Object.keys(MAIN_MODULES).forEach(k => {
      const Module = MAIN_MODULES[k];

      if (!Module.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Module.selector);

      if (!block) {
        return;
      }

      if (block.dataset["instanceIndex" + Module.name]) {
        return;
      }

      const previousIndexes = block.dataset.instanceIndex;
      block.dataset.instanceIndex = previousIndexes
        ? [previousIndexes, this.lastIndex].join(",")
        : this.lastIndex;
      block.dataset["instanceIndex" + Module.name] = this.lastIndex;
      this.lastIndex++;

      this[k] = new Module(block);

      newInstances.push(this[k]);
    });

    // Call onReady method if any
    newInstances.forEach(instance => {
      if (instance.onReady) {
        instance.onReady();
      }
    });

    // Call onComplete method if any
    newInstances.forEach(instance => {
      if (instance.onComplete) {
        instance.onComplete();
      }
    });

    this.instances = this.instances.concat(newInstances);

    return newInstances;
  };

  destroyArea = (parentElement, keep = []) => {
    [...parentElement.children].forEach(element => {
      // Skip destroy if element is in `keep`
      if (keep.some(s => element.matches && element.matches(s))) {
        return;
      }

      // If this element has an instance attached
      if (element.dataset.instanceIndex) {
        // Get instance
        const instances = getInstances(element);

        instances.forEach(instance => {
          // Call onDestroy method if any
          if (instance.onDestroy) {
            instance.onDestroy();
          }

          // Remove instance references
          element.dataset.instanceIndex.split(",").map(i => {
            this.instances[parseInt(i, 10)] = null;
          });
          Object.keys(MAIN_MODULES).forEach(k => {
            if (this[k] === instance) {
              this[k] = null;
            }
          });
          Object.keys(MODALS).forEach(k => {
            if (this.modals[k] === instance) {
              this.modals[k] = null;
            }
          });
        });
      }

      element.remove();
    });
  };

  onRecaptchaReady() {
    if (!this.instances) {
      return;
    }

    this.instances.forEach(instance => instance?.onRecaptchaReady?.());
  }

  onResize = e => {
    if (!this.instances) {
      return;
    }

    this.instances.forEach(instance => instance?.onResize?.(e));
  };

  onScroll = e => {
    if (!this.instances) {
      return;
    }

    this.instances.forEach(instance => instance?.onScroll?.(e));
  };
}

window.App = new App();
